import { clamp } from './clamp';

/** Separable two-pass box blur — O(width*height) regardless of radius */
export function boxBlur(source: ImageData, radius: number): ImageData {
  if (radius <= 0) return source;

  const { width, height } = source;
  const src = new Uint8ClampedArray(source.data);
  const tmp = new Uint8ClampedArray(src.length);
  const dst = new Uint8ClampedArray(src.length);

  const r = Math.round(radius);
  const diam = r * 2 + 1;

  // Horizontal pass: src -> tmp
  for (let y = 0; y < height; y++) {
    let ri = 0, gi = 0, bi = 0, ai = 0;

    // Initialize window for x = 0
    for (let k = -r; k <= r; k++) {
      const sx = clamp(k, 0, width - 1);
      const idx = (y * width + sx) * 4;
      ri += src[idx];
      gi += src[idx + 1];
      bi += src[idx + 2];
      ai += src[idx + 3];
    }

    for (let x = 0; x < width; x++) {
      const di = (y * width + x) * 4;
      tmp[di] = ri / diam;
      tmp[di + 1] = gi / diam;
      tmp[di + 2] = bi / diam;
      tmp[di + 3] = ai / diam;

      // Slide window
      const addX = clamp(x + r + 1, 0, width - 1);
      const remX = clamp(x - r, 0, width - 1);
      const addIdx = (y * width + addX) * 4;
      const remIdx = (y * width + remX) * 4;
      ri += src[addIdx] - src[remIdx];
      gi += src[addIdx + 1] - src[remIdx + 1];
      bi += src[addIdx + 2] - src[remIdx + 2];
      ai += src[addIdx + 3] - src[remIdx + 3];
    }
  }

  // Vertical pass: tmp -> dst
  for (let x = 0; x < width; x++) {
    let ri = 0, gi = 0, bi = 0, ai = 0;

    // Initialize window for y = 0
    for (let k = -r; k <= r; k++) {
      const sy = clamp(k, 0, height - 1);
      const idx = (sy * width + x) * 4;
      ri += tmp[idx];
      gi += tmp[idx + 1];
      bi += tmp[idx + 2];
      ai += tmp[idx + 3];
    }

    for (let y = 0; y < height; y++) {
      const di = (y * width + x) * 4;
      dst[di] = ri / diam;
      dst[di + 1] = gi / diam;
      dst[di + 2] = bi / diam;
      dst[di + 3] = ai / diam;

      // Slide window
      const addY = clamp(y + r + 1, 0, height - 1);
      const remY = clamp(y - r, 0, height - 1);
      const addIdx = (addY * width + x) * 4;
      const remIdx = (remY * width + x) * 4;
      ri += tmp[addIdx] - tmp[remIdx];
      gi += tmp[addIdx + 1] - tmp[remIdx + 1];
      bi += tmp[addIdx + 2] - tmp[remIdx + 2];
      ai += tmp[addIdx + 3] - tmp[remIdx + 3];
    }
  }

  return new ImageData(dst, width, height);
}
